import { select } from "redux-saga/effects";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { getLocalTimeZone } from "@app/util/dates";
import type FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { formatDate } from "@app/util/formatters";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { EfficiencyMetricDescriptor } from "./getEfficiencyMetricDescriptor";

export interface EfficiencyDrillDownStats {
  distance: number;
  metricTotal: number;
}

type SumAggregations = Record<string, { value: number }> | undefined;

const fetchEfficiencyDrillDownStats = function* (params: {
  columns: ViewState["columns"];
  descriptor: EfficiencyMetricDescriptor;
  fieldModel: FieldModel;
  filterModel: FilterModel;
  range: DateRange;
  userIds: User["id"][];
}): Generator<unknown, EfficiencyDrillDownStats, never> {
  const { columns, descriptor, fieldModel, filterModel, range, userIds } = params;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const $filters = {
    includeAccessStatus: true,
    ...convertToPlatformFilterModel(filterModel, columns, fieldModel),
  };

  let metricTotal = 0;
  if (descriptor.valueKind === "field" && descriptor.field) {
    const response: AggregatedListResponse<AnyEntity, SumAggregations> = yield callApi(
      descriptor.fetchMethod,
      orgId,
      { $aggs: { sum: [descriptor.field] }, $filters, $limit: 0 }
    );
    metricTotal = response.aggregations?.[`sum_${descriptor.field}`]?.value ?? 0;
  } else {
    const response: ListResponse<AnyEntity> = yield callApi(descriptor.fetchMethod, orgId, {
      $filters,
      $limit: 0,
    });
    metricTotal = response.total;
  }

  const distanceFilters = {
    date: {
      $gte: formatDate(range.startDate, "yyyy-MM-dd"),
      $lte: formatDate(range.endDate, "yyyy-MM-dd"),
      $timezone: getLocalTimeZone(),
    },
    ...(userIds.length ? { userId: { $in: userIds } } : {}),
  };
  const distanceResponse: ListResponse<DistanceRecord> = yield callApi("fetchDistance", orgId, {
    $filters: distanceFilters,
  });
  const distance = distanceResponse.data.reduce((total, record) => total + record.totalDistance, 0);

  return { distance, metricTotal };
};

export default fetchEfficiencyDrillDownStats;
